import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { BottomNavService, OnAppInit, ViewTransitionUtils } from '@frontend/vanilla/core';
import { PLATFORM } from '@frontend/vanilla/ssr';
import { combineLatest, distinctUntilKeyChanged, switchMap, withLatestFrom } from 'rxjs';

import { BetslipBarVisibilityService } from '../../betslip/modules/betslip-bar/services/betslip-bar-visibility.service';
import { DispatcherEvent } from '../event-details-common/event-details-common.models';

@Injectable({ providedIn: 'root' })
export class ToggleBottomNavService implements OnAppInit {
    private adaptiveLayout = inject(AdaptiveLayoutService);
    private bottomNavService = inject(BottomNavService);
    private betslipBarVisibility = inject(BetslipBarVisibilityService);
    private dispatcher = inject(DispatcherService);
    private productUtils = inject(SportsProductUtils);

    private readonly destroyRef = inject(DestroyRef);
    private platform = inject(PLATFORM);

    readonly #viewTransitionUtils = inject(ViewTransitionUtils);

    onAppInit(): void {
        const layoutBottomNavVisibility = this.adaptiveLayout.stateChange$.pipe(distinctUntilKeyChanged('bottomNavigation'));
        const vanillaNav = this.bottomNavService.whenReady.pipe(switchMap(() => this.bottomNavService.isEnabled()));

        // Reset bottom nav to show after we leave sports, only after bottom nav is triggered
        combineLatest([this.productUtils.fromSportsLeave(), this.bottomNavService.whenReady])
            .pipe(this.#viewTransitionUtils.deferUntilTransitionReady(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.bottomNavService.show();
            });

        combineLatest([vanillaNav, layoutBottomNavVisibility])
            .pipe(
                this.productUtils.filterSportsEmitLast(),
                this.#viewTransitionUtils.deferUntilTransitionReady(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(([vanillaNavEnabled, state]) => {
                if (!vanillaNavEnabled) {
                    return;
                }

                if (this.platform.isServerRendered) {
                    // if coming from ssr, check only the flags part and not directly,
                    // also check for true or false only
                    if (state.flags.bottomNavigation === true) {
                        this.bottomNavService.show();
                        this.betslipBarVisibility.showComponent();
                    }
                    if (state.flags.bottomNavigation === false) {
                        this.bottomNavService.hide();
                        this.betslipBarVisibility.hideComponent();
                    }
                } else {
                    if (state.bottomNavigation) {
                        this.bottomNavService.show();
                        this.betslipBarVisibility.showComponent();
                    } else {
                        this.bottomNavService.hide();
                        this.betslipBarVisibility.hideComponent();
                    }
                }
            });

        this.dispatcher
            .on<boolean>(DispatcherEvent.EventDetails.Expanded)
            .pipe(withLatestFrom(vanillaNav, layoutBottomNavVisibility))
            .subscribe(([expanded, enabled, state]) => {
                if (!enabled || !state.bottomNavigation) {
                    return;
                }

                if (expanded) {
                    this.bottomNavService.hide();
                    this.betslipBarVisibility.hideComponent();
                } else {
                    this.bottomNavService.show();
                    this.betslipBarVisibility.showComponent();
                }
            });
    }
}
